import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AppState, SaaSUser, Project, RTMSession, UsageStats, DashboardStats, UIState } from '../types';

const initialUIState: UIState = {
  currentPage: 'dashboard',
  sidebarCollapsed: false,
  theme: 'light',
  language: 'ja',
  notifications: [],
  loading: {},
  errors: {},
};

const initialUsageStats: UsageStats = {
  totalSessions: 0,
  totalMinutes: 0,
  totalTranscripts: 0,
  totalSuggestions: 0,
  totalResearchQueries: 0,
  apiCalls: 0,
  currentPeriod: {
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    sessions: 0,
    minutes: 0,
    apiCalls: 0,
    cost: 0,
  },
  previousPeriod: {
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
    sessions: 0,
    minutes: 0,
    apiCalls: 0,
    cost: 0,
  },
};

const initialDashboardStats: DashboardStats = {
  activeSessions: 0,
  totalUsersToday: 0,
  successRate: 0,
  avgSessionDuration: 0,
  realtimeMetrics: {
    currentConnections: 0,
    messagesPerSecond: 0,
    avgLatency: 0,
    errorRate: 0,
  },
};

export const useAppStore = create<AppState>()(
  devtools(
    persist(
      (set) => ({
        // State
        user: null,
        currentProject: null,
        projects: [],
        realtimeSession: null,
        usage: initialUsageStats,
        dashboard: initialDashboardStats,
        ui: initialUIState,

        // Actions
        setUser: (user: SaaSUser | null) => {
          set(
            { user },
            false,
            'setUser'
          );
        },

        setCurrentProject: (project: Project | null) => {
          set(
            { currentProject: project },
            false,
            'setCurrentProject'
          );
        },

        setProjects: (projects: Project[]) => {
          set(
            { projects },
            false,
            'setProjects'
          );
        },

        setRealtimeSession: (session: RTMSession | null) => {
          set(
            { realtimeSession: session },
            false,
            'setRealtimeSession'
          );
        },

        updateUsage: (usage: UsageStats) => {
          set(
            { usage },
            false,
            'updateUsage'
          );
        },

        updateDashboard: (dashboard: DashboardStats) => {
          set(
            { dashboard },
            false,
            'updateDashboard'
          );
        },

        updateUI: (uiUpdates: Partial<UIState>) => {
          set(
            (state) => ({
              ui: { ...state.ui, ...uiUpdates }
            }),
            false,
            'updateUI'
          );
        },

        // Helper actions
        setLoading: (key: string, loading: boolean) => {
          set(
            (state) => ({
              ui: {
                ...state.ui,
                loading: { ...state.ui.loading, [key]: loading }
              }
            }),
            false,
            'setLoading'
          );
        },

        setError: (key: string, error: string) => {
          set(
            (state) => ({
              ui: {
                ...state.ui,
                errors: { ...state.ui.errors, [key]: error }
              }
            }),
            false,
            'setError'
          );
        },

        clearError: (key: string) => {
          set(
            (state) => {
              const newErrors = { ...state.ui.errors };
              delete newErrors[key];
              return {
                ui: {
                  ...state.ui,
                  errors: newErrors
                }
              };
            },
            false,
            'clearError'
          );
        },

        addNotification: (notification: Omit<UIState['notifications'][0], 'id'>) => {
          const id = Date.now().toString();
          set(
            (state) => ({
              ui: {
                ...state.ui,
                notifications: [...state.ui.notifications, { ...notification, id }]
              }
            }),
            false,
            'addNotification'
          );
        },

        removeNotification: (id: string) => {
          set(
            (state) => ({
              ui: {
                ...state.ui,
                notifications: state.ui.notifications.filter(n => n.id !== id)
              }
            }),
            false,
            'removeNotification'
          );
        },

        markNotificationAsRead: (id: string) => {
          set(
            (state) => ({
              ui: {
                ...state.ui,
                notifications: state.ui.notifications.map(n => 
                  n.id === id ? { ...n, read: true } : n
                )
              }
            }),
            false,
            'markNotificationAsRead'
          );
        },
      }),
      {
        name: 'rtm-saas-storage', // Storage key
        partialize: (state) => ({
          user: state.user,
          currentProject: state.currentProject,
          ui: {
            theme: state.ui.theme,
            language: state.ui.language,
            sidebarCollapsed: state.ui.sidebarCollapsed,
          }
        }),
      }
    ),
    { name: 'RTM SaaS Store' }
  )
);

// Helper hooks for specific parts of the store
export const useUser = () => useAppStore((state) => state.user);
export const useCurrentUser = () => useAppStore((state) => state.user);
export const useProjects = () => useAppStore((state) => state.projects);
export const useCurrentProject = () => useAppStore((state) => state.currentProject);
export const useRealtimeSession = () => useAppStore((state) => state.realtimeSession);
export const useUsageStats = () => useAppStore((state) => state.usage);
export const useDashboardStats = () => useAppStore((state) => state.dashboard);
export const useUI = () => useAppStore((state) => state.ui);

// Typed versions for actions with specific error handling
export const useAppActions = () => {
  const store = useAppStore();
  
  return {
    setUser: store.setUser,
    setCurrentProject: store.setCurrentProject,
    setProjects: store.setProjects,
    setRealtimeSession: store.setRealtimeSession,
    updateUsage: store.updateUsage,
    updateDashboard: store.updateDashboard,
    updateUI: store.updateUI,
    setLoading: store.setLoading,
    setError: store.setError,
    clearError: store.clearError,
    addNotification: store.addNotification,
    removeNotification: store.removeNotification,
    markNotificationAsRead: store.markNotificationAsRead,
  };
};